import React from "react";
import PropTypes from "prop-types";
import SwiperCore, { Pagination, Navigation, Keyboard } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import NavigationLeftSelected from "@/icons/Navigation/Left/Selected";
import NavigationRightSelected from "@/icons/Navigation/Right/Selected";

import { useUniqueId } from "../hooks/useUniqueId";

import { A11yText } from "../A11yText";
import { Icon } from "../Icon";

import styles from "./TariffTileSlider.scss";

SwiperCore.use([Pagination, Navigation, Keyboard]);

export const TariffTileSlider = (props) => {
  const { children, a11yPrevButton, a11yNextButton, id, ...restProps } = props;

  const uid = useUniqueId("TariffTileSlider", props);

  const params = {
    spaceBetween: 24,
    slidesPerView: "auto",
    navigation: {
      prevEl: `#prev-button-${id || uid}`,
      nextEl: `#next-button-${id || uid}`,
      disabledClass: styles.buttonDisabled,
    },
    pagination: {
      el: `#pagination-${id || uid}`,
      lockClass: styles.paginationLock,
      bulletActiveClass: styles.paginationBulletActive,
      clickable: true,
    },
    keyboard: {
      enabled: true,
    },
    roundLengths: true,
    centerInsufficientSlides: true,
    watchOverflow: true,
    threshold: 3,
  };

  const handleSlideChange = () => {
    [...document.querySelectorAll("[data-tooltip='true']")].forEach((node) => {
      // eslint-disable-next-line no-underscore-dangle
      const tooltipInstance = node._tippy;

      if (tooltipInstance) {
        tooltipInstance.hide();
      }
    });
  };

  return (
    <div className={styles.sliderWrapper}>
      <button
        type="button"
        id={`prev-button-${id || uid}`}
        className={styles.prevButton}
      >
        <Icon
          icon={NavigationLeftSelected}
          size={Icon.Size.medium}
          wrapperProps={{
            style: { height: "auto", position: "relative", top: 1, right: -1 },
          }}
        />
        <A11yText>{a11yPrevButton}</A11yText>
      </button>

      <Swiper
        {...params}
        {...restProps}
        className={styles.slider}
        tabIndex={0}
        onSlideChange={handleSlideChange}
        onSliderMove={handleSlideChange}
      >
        {React.Children.toArray(children).map((child) => (
          <SwiperSlide key={child.key}>{child}</SwiperSlide>
        ))}

        <div className={styles.pagination} id={`pagination-${id || uid}`} />
      </Swiper>

      <button
        type="button"
        id={`next-button-${id || uid}`}
        className={styles.nextButton}
      >
        <Icon
          icon={NavigationRightSelected}
          size={Icon.Size.medium}
          wrapperProps={{
            style: { height: "auto", position: "relative", top: 1, left: -2 },
          }}
        />
        <A11yText>{a11yNextButton}</A11yText>
      </button>
    </div>
  );
};

TariffTileSlider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  a11yPrevButton: PropTypes.string,
  a11yNextButton: PropTypes.string,
  id: PropTypes.string,
};

TariffTileSlider.defaultProps = {
  a11yPrevButton: "previous button",
  a11yNextButton: "next button",
  id: undefined,
};

TariffTileSlider.displayName = "TariffTileSlider";
